/**
 * S-44 «Прибыль за период» — ФАКТИЧЕСКАЯ прибыль по дням и моделям (админ 23.10.2026: «себестоимость — константа
 * до следующего привоза, а цена, реклама, логистика меняются каждый день — пусть по вчерашнему отчёту считает
 * вчерашнюю прибыль»).
 *
 * Постоянное — себестоимость по партиям (с даты прихода) и прочие расходы из калькулятора. Цена, комиссия,
 * логистика — из отчётов WB о реализации (недельных, дальше ежедневных), реклама — фактический расход за день,
 * НДС — по правилам калькулятора. Дни без отчёта (сегодня) — оперативная статистика продаж, предварительно.
 * Штрафы и удержания WB без привязки к товару — отдельными строками и в итоге «с учётом».
 */
import { useState } from 'react'
import { fetchProfit, type ProfitSlice } from '../api/sections.js'
import type { ScreenId } from '../router/screens.js'
import { Icon } from '../components/icons/Icon.js'
import { Empty, Failed, Loading, NoShop, useData, useScreenHead } from './common.js'
import { Sect } from './ui.js'
import { capitalize } from './words.js'
import { useTwoCols } from '../wide.js'

type Open = (id: ScreenId, param?: string) => void
type Range = { from: string; to: string }

const MINUS = '−'
const ISO = /^\d{4}-\d{2}-\d{2}$/
// Неразрывный пробел перед «₽» и «шт.»: они не уезжают на отдельную строку.
const NBSP = String.fromCharCode(160)
const rub = (n: number): string => {
  const r = Math.round(n)
  return (r < 0 ? MINUS : '') + Math.abs(r).toLocaleString('ru-RU') + NBSP + '₽'
}
const units = (n: number): string => n.toLocaleString('ru-RU') + NBSP + 'шт.'
const pct = (n: number | null): string => (n === null ? '—' : (n < 0 ? MINUS : '') + Math.abs(n).toLocaleString('ru-RU', { maximumFractionDigits: 1 }) + NBSP + '%')

/** «2026-10-12_2026-10-18» → период; иначе null — по умолчанию вчера. */
function parseRange(p: string | null): Range | null {
  if (p === null) return null
  const [from = '', to = ''] = p.split('_')
  return ISO.test(from) && ISO.test(to) && from <= to ? { from, to } : null
}

export function ScreenProfit({ period, open }: { period: string | null; open: Open }): React.JSX.Element {
  const [range, setRange] = useState<Range | null>(() => parseRange(period))
  const { s, reload } = useData(() => fetchProfit(range?.from ?? null, range?.to ?? null), [range?.from, range?.to])
  const d = s.kind === 'ok' && s.data.shop !== null ? s.data : null
  useScreenHead('Прибыль за период', d === null ? null : d.period_display)
  if (s.kind === 'loading') return <Loading label="Считаю прибыль за период" rows={8} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  if (d === null) return <NoShop reason={s.data.reason} />
  return <ProfitBody key={`${d.from}_${d.to}`} d={d} open={open} onRange={setRange} />
}

function ProfitBody({ d, open, onRange }: { d: ProfitSlice; open: Open; onRange: (r: Range) => void }): React.JSX.Element {
  const [from, setFrom] = useState(d.from)
  const [to, setTo] = useState(d.to)
  const two = useTwoCols()
  const draftOk = ISO.test(from) && ISO.test(to) && from <= to
  const changed = from !== d.from || to !== d.to
  const max = Math.max(1, ...d.rows.map((r) => Math.abs(r.profit_rub)))
  const t = d.total
  const nothingKnown = d.products.length === 0 && t.unknown_units !== 0
  const line = (label: string, v: number, sum = false): React.JSX.Element | null =>
    !sum && Math.round(v) === 0 ? null : <p className={sum ? 'calc-row calc-row-sum' : 'calc-row'} key={label}><span>{label}</span><span>{rub(v)}</span></p>

  const totalCard = (
    <section className="card card-pad calc-result" data-testid="pf-total">
      <p className="row-meta">Заработали {d.period_display} — по отчётам WB и вашей себестоимости</p>
      {/* Ни одной модели с себестоимостью — прибыль не известна, а не ноль. */}
      {nothingKnown
        ? <p className="calc-profit calc-unknown" data-testid="pf-unknown">—</p>
        : <p className={'calc-profit' + (t.profit_rub < 0 ? ' calc-loss' : '')} data-testid="pf-profit">{rub(t.profit_rub)}</p>}
      <p className="body-text">продано {units(t.net_units)} за вычетом возвратов{t.net_units > 0 && t.revenue > 0 ? ` · средняя цена ${rub(t.revenue / t.net_units)}` : ''}</p>
      {t.prelim_rub !== 0 && <p className="row-meta gap-top-s" data-testid="pf-prelim">из них {rub(t.prelim_rub)} — предварительно, по оперативной статистике WB</p>}
      {!nothingKnown && (
        <div className="calc-break gap-top-s" data-testid="pf-break">
          {line('Выручка по цене продавца', t.revenue, true)}
          {line('Комиссия и эквайринг WB', -(t.revenue - t.to_transfer))}
          {line('Логистика, хранение и прочие услуги WB', -t.wb)}
          {line('Себестоимость, упаковка, доставка', -t.cogs)}
          {line('Реклама', -t.ads)}
          {line('НДС к уплате', -t.vat)}
          {line('Прочие расходы', -t.other)}
          {line('Прибыль по моделям', t.profit_rub, true)}
          {line('Удержания WB без привязки к товару', t.shop_net)}
          {line('Штрафы WB', -t.penalty_rub)}
          {(Math.round(t.shop_net) !== 0 || Math.round(t.penalty_rub) !== 0) && line('Итого с учётом штрафов и удержаний', t.result_rub, true)}
        </div>
      )}
      {t.unknown_units !== 0 && (
        <p className="calc-warn" data-testid="pf-missing-note">
          Без себестоимости продано {units(t.unknown_units)} ({t.unknown_models === 1 ? 'одна модель' : `моделей: ${t.unknown_models}`}) — их прибыль в сумму не вошла. Введите себестоимость в калькуляторе.
        </p>
      )}
      {d.data_note !== null && <p className="row-meta gap-top-s" data-testid="pf-data-note">{d.data_note}</p>}
    </section>
  )

  const daysCard = d.rows.length === 0 ? null : <>
    <Sect title={d.group === 'day' ? 'По дням' : 'По неделям'} aside="шт. · прибыль" />
    <section className="card card-pad pf-days" data-testid="pf-rows">
      {d.rows.map((r) => (
        <div className={'pf-day' + (r.prelim ? ' pf-day-prelim' : '')} key={r.key}>
          <span className="pf-day-label">{r.label}{r.prelim && <span className="pf-tag" title="предварительно">≈</span>}</span>
          <span className="pf-bar" aria-hidden="true">
            <span className={r.profit_rub < 0 ? 'pf-bar-neg' : ''} style={{ width: `${Math.round((100 * Math.abs(r.profit_rub)) / max)}%` }} />
          </span>
          <span className="pf-day-units">{r.net_units}</span>
          <span className="pf-day-val">{rub(r.profit_rub)}{r.missing_units !== 0 ? '*' : ''}</span>
        </div>
      ))}
      {d.rows.some((r) => r.prelim) && <p className="row-meta gap-top-s">≈ предварительно: оперативная статистика продаж WB, уточнится с отчётом о реализации.</p>}
      {d.rows.some((r) => r.missing_units !== 0) && <p className="row-meta gap-top-s">* в этот день продавались и модели без себестоимости — их прибыль не вошла.</p>}
      <p className="row-meta gap-top-s">Прибыль дня — без штрафов и удержаний без привязки к товару: они — в итоге выше.</p>
    </section>
  </>

  const modelsCard = <>
    {d.products.length > 0 && <>
      <Sect title="По моделям" aside="прибыль" />
      <section className="card" data-testid="pf-products">
        {d.products.map((p) => (
          <button type="button" className="row row-tap" key={p.nm_id} data-testid={'pf-nm-' + p.nm_id} onClick={() => open('S-27', String(p.nm_id))}>
            <span className="row-main">
              <span className="row-title">{p.title}{p.prelim && <span className="pf-tag" title="предварительно">≈</span>}</span>
              <span className="row-meta">
                {units(p.net_units)}{p.avg_price !== null ? ` · средняя цена ${rub(p.avg_price)}` : ''}{p.margin_pct !== null ? ` · маржа ${pct(p.margin_pct)}` : ''}
              </span>
              {p.per_unit !== null && (
                <span className="row-meta">со штуки {rub(p.per_unit)}{p.plan_per_unit !== null ? ` · по калькулятору ${rub(p.plan_per_unit)}` : ''}</span>
              )}
              {p.batches.length > 1 && p.batches.map((b) => (
                <span className="row-meta pf-seg" key={b.since}>{capitalize(b.since)}: себестоимость {rub(b.unit_cost)} · {units(b.units)}</span>
              ))}
              {p.batches.length === 1 && <span className="row-meta">себестоимость {rub(p.batches[0]!.unit_cost)}</span>}
            </span>
            <span className="row-val"><span className={'num' + ((p.profit_rub ?? 0) < 0 ? ' calc-loss' : '')}>{p.profit_rub === null ? '—' : rub(p.profit_rub)}</span></span>
            <Icon name="nav-next" className="ic-chev row-end" />
          </button>
        ))}
      </section>
    </>}
    {d.missing.length > 0 && <>
      <Sect title="Без себестоимости" aside="продано" />
      <section className="card" data-testid="pf-missing">
        {d.missing.map((m) => (
          <button type="button" className="row row-tap" key={m.nm_id} onClick={() => open('S-27', String(m.nm_id))}>
            <span className="row-main"><span className="row-title">{m.title}</span><span className="row-meta">ввести себестоимость</span></span>
            <span className="row-val"><span className="num">{units(m.net_units)}</span></span>
            <Icon name="nav-next" className="ic-chev row-end" />
          </button>
        ))}
      </section>
    </>}
  </>

  return (
    <div data-testid="screen-body" className="calc">
      <div className="segs" role="group" aria-label="Период">
        {d.presets.map((p) => {
          const on = p.from === d.from && p.to === d.to
          return (
            <button type="button" key={p.key} className={'seg' + (on ? ' seg-on' : '')} aria-pressed={on} data-testid={'pf-' + p.key}
              onClick={() => onRange({ from: p.from, to: p.to })}>{p.label}</button>
          )
        })}
      </div>
      <section className="card card-pad gap-top pf-range">
        <label className="pf-date"><span className="row-meta">с</span>
          <input type="date" className="inp" value={from} max={d.today} data-testid="pf-from" onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="pf-date"><span className="row-meta">по</span>
          <input type="date" className="inp" value={to} max={d.today} data-testid="pf-to" onChange={(e) => setTo(e.target.value)} /></label>
        {changed && (
          <button type="button" className="btn btn-block gap-top-s" disabled={!draftOk} data-testid="pf-apply" onClick={() => onRange({ from, to })}>
            {draftOk ? 'Показать' : 'Начало — не позже конца'}
          </button>
        )}
      </section>

      {d.rows.length === 0 && d.products.length === 0 && d.missing.length === 0
        ? <><div className="gap-top">{totalCard}</div><div className="empty-card gap-top"><Empty icon="section-cost" title="Продаж за этот период нет" text={d.data_note ?? 'Выберите другой период.'} /></div></>
        : two
          // Широкое окно: слева итог и дни, справа модели; узкое — одной колонкой.
          ? <div className="cols"><div className="col">{totalCard}{daysCard}</div><div className="col">{modelsCard}</div></div>
          : <div className="gap-top">{totalCard}{daysCard}{modelsCard}</div>}
      {/* Архив месяца в Excel (заказчик 02.11.2026): WB свои отчёты со временем прячет — держим копию у себя. */}
      <button type="button" className="link-inline gap-top-s" data-testid="pf-archive" onClick={() => open('S-45')}>
        Архив месяцев: скачать Excel за месяц →
      </button>
      <p className="hint">
        Постоянна только себестоимость — по партиям, с даты прихода (калькулятор → «Новая партия с даты»). Цена, комиссия, логистика —
        из отчётов WB о реализации каждого дня, реклама — фактический расход кампаний, НДС — 22/122 с вычетами. Пока отчёта за день нет
        (сегодня) — оперативная статистика продаж WB, предварительно.
      </p>
    </div>
  )
}
